// components/ui/Pagination.tsx
import React from 'react'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  siblingCount?: number
  boundaryCount?: number
  showFirstLast?: boolean
  showPrevNext?: boolean
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'minimal' | 'compact'
  className?: string
  showInfo?: boolean
  infoTemplate?: string
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  showFirstLast = true,
  showPrevNext = true,
  size = 'md',
  variant = 'default',
  className = '',
  showInfo = false,
  infoTemplate = 'Page {current} of {total}'
}) => {
  if (totalPages <= 1) return null

  const sizeClasses = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'h-3 w-3',
      info: 'text-xs'
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'h-4 w-4',
      info: 'text-sm'
    },
    lg: {
      button: 'px-4 py-2 text-base',
      icon: 'h-5 w-5',
      info: 'text-base'
    }
  }

  const variantClasses = {
    default: 'bg-gray-800 hover:bg-gray-700 border border-gray-700',
    minimal: 'bg-transparent hover:bg-gray-800',
    compact: 'bg-gray-800 hover:bg-gray-700'
  }

  const activeClasses = 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'

  // Generate page numbers to show
  const range = (start: number, end: number) => {
    const length = end - start + 1
    return Array.from({ length }, (_, i) => start + i)
  }

  const getPageNumbers = () => {
    const totalNumbers = siblingCount * 2 + 3
    const totalBlocks = totalNumbers + 2

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - siblingCount)
      const endPage = Math.min(totalPages - 1, currentPage + siblingCount)

      let pages: (number | string)[] = range(startPage, endPage)

      const hasLeftSpill = startPage > 2
      const hasRightSpill = totalPages - endPage > 1
      const spillOffset = totalNumbers - (pages.length + 1)

      switch (true) {
        case hasLeftSpill && !hasRightSpill:
          const extraPages = range(startPage - spillOffset, startPage - 1)
          pages = [1, 'ellipsis-left', ...extraPages, ...pages]
          break
        case !hasLeftSpill && hasRightSpill:
          const extraPagesRight = range(endPage + 1, endPage + spillOffset)
          pages = [...pages, ...extraPagesRight, 'ellipsis-right', totalPages]
          break
        case hasLeftSpill && hasRightSpill:
        default:
          pages = [1, 'ellipsis-left', ...pages, 'ellipsis-right', totalPages]
          break
      }

      return [1, ...pages, totalPages]
    }

    return range(1, totalPages)
  }

  const pages = getPageNumbers()

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page)
    }
  }

  const renderPageButton = (page: number | string, index: number) => {
    if (page === 'ellipsis-left' || page === 'ellipsis-right') {
      return (
        <span
          key={`ellipsis-${index}`}
          className={`${sizeClasses[size].button} flex items-center justify-center text-gray-400`}
        >
          <MoreHorizontal className={sizeClasses[size].icon} />
        </span>
      )
    }

    const pageNum = page as number
    const isActive = pageNum === currentPage

    return (
      <button
        key={`page-${pageNum}`}
        onClick={() => handlePageChange(pageNum)}
        className={`
          ${sizeClasses[size].button}
          ${variantClasses[variant]}
          ${isActive ? activeClasses : 'text-gray-300'}
          rounded-md transition-colors duration-200 font-medium
          min-w-[2.5rem] flex items-center justify-center
        `}
        disabled={isActive}
        aria-current={isActive ? 'page' : undefined}
      >
        {pageNum.toLocaleString()}
      </button>
    )
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {showInfo && (
        <div className={`${sizeClasses[size].info} text-gray-400`}>
          {infoTemplate
            .replace('{current}', currentPage.toLocaleString())
            .replace('{total}', totalPages.toLocaleString())}
        </div>
      )}

      <div className="flex items-center gap-1">
        {showFirstLast && (
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className={`
              ${sizeClasses[size].button}
              ${variantClasses[variant]}
              text-gray-300 rounded-md transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-1
            `}
            aria-label="First page"
          >
            <ChevronLeft className={sizeClasses[size].icon} />
            <ChevronLeft className={sizeClasses[size].icon} style={{ marginLeft: '-0.25rem' }} />
          </button>
        )}

        {showPrevNext && (
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`
              ${sizeClasses[size].button}
              ${variantClasses[variant]}
              text-gray-300 rounded-md transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-1
            `}
            aria-label="Previous page"
          >
            <ChevronLeft className={sizeClasses[size].icon} />
            <span className="hidden sm:inline">Previous</span>
          </button>
        )}

        <div className="flex items-center gap-1">
          {pages.map((page, index) => renderPageButton(page, index))}
        </div>

        {showPrevNext && (
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`
              ${sizeClasses[size].button}
              ${variantClasses[variant]}
              text-gray-300 rounded-md transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-1
            `}
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className={sizeClasses[size].icon} />
          </button>
        )}

        {showFirstLast && (
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={`
              ${sizeClasses[size].button}
              ${variantClasses[variant]}
              text-gray-300 rounded-md transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-1
            `}
            aria-label="Last page"
          >
            <ChevronRight className={sizeClasses[size].icon} />
            <ChevronRight className={sizeClasses[size].icon} style={{ marginLeft: '-0.25rem' }} />
          </button>
        )}
      </div>

      {/* Quick jump for larger paginations */}
      {variant === 'default' && totalPages > 10 && (
        <div className="flex items-center gap-2">
          <span className={`${sizeClasses[size].info} text-gray-400`}>Go to:</span>
          <input
            type="number"
            min="1"
            max={totalPages}
            defaultValue={currentPage}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const page = parseInt(e.currentTarget.value)
                if (page >= 1 && page <= totalPages) {
                  handlePageChange(page)
                  e.currentTarget.value = ''
                }
              }
            }}
            className={`
              ${sizeClasses[size].button}
              bg-gray-700 border border-gray-600 rounded-md text-white
              w-16 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
              [&::-webkit-inner-spin-button]:appearance-none
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            `}
          />
        </div>
      )}
    </div>
  )
}

// Simple pagination for minimal use cases
export const SimplePagination: React.FC<{
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
}> = ({ currentPage, totalPages, onPageChange, className = '' }) => {
  if (totalPages <= 1) return null

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 rounded text-sm transition-colors"
      >
        Previous
      </button>
      <span className="text-sm text-gray-400">
        Page <span className="text-white font-medium">{currentPage}</span> of{' '}
        <span className="text-white font-medium">{totalPages}</span>
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 rounded text-sm transition-colors"
      >
        Next
      </button>
    </div>
  )
}